import { Command as CommandPrimitive } from "cmdk";
import {
  AddCircle,
  Clock,
  Eye,
  EyeSlash,
  GalleryEdit,
  MinusCirlce,
} from "iconsax-react";
import { Check, ChevronDown } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import { CgColorPicker } from "react-icons/cg";
import { useDebounce } from "src/hooks/useDebounce";
import { cn } from "src/lib/utils";
import { uploadFile } from "src/services/UploadFile.service";
import { apiErrorHandler } from "src/utils/apiErrorHandler";
import { capitalize, numberWithCommas } from "src/utils/commons";
import {
  convertCurrencyToString,
  convertToCurrency,
} from "src/utils/convertToCurrency";
import { formatInputPhoneNumber } from "src/utils/formatPhoneNumber";
import { getCountryCodes } from "src/utils/getCountries";
import { camelToSnake } from "src/utils/snackToCamel";
import DateRangePicker from "../SearchInput/DateRangePicker";
import Spinner from "../Spinner";
import { Button } from "../ui/button";
import { ColorPicker } from "../ui/color-picker";
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../ui/command";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../ui/form";
import { Input, InputProps } from "../ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Textarea } from "../ui/textarea";
import useCustomInputHandler from "./hooks/useCustomInputHandler";
import { MultiSelect } from "../ui/multi-select";
import { USER_TYPE_BACKGROUND } from "src/constant";
import { BuyerType } from "src/types/apiResponse";
import { SalespersonContract } from "src/pages/UserManagement/CustomerList/api/interfaces";
import { getSalespersonContractPeriod } from "src/pages/UserManagement/SalespersonList/utils/getSalespersonContractPeriod";
import CreateSelectionNewDataModal, {
  CREATE_SELECTION_NEW_DATA_TYPE,
  CreateSelectionNewDataModalProps,
} from "./CreateSelectionNewDataModal";

export type CustomInputOptions = {
  label: string;
  value: string | number;
  userType?: BuyerType;
  customerType?: string;
  typeHex?: string;
  isSalespersonAssigned?: boolean;
  activeContracts?: SalespersonContract[];
};

interface CustomInputProps extends InputProps {
  form: any;
  name: string;
  labelText?: string;
  description?: string;
  textArea?: boolean;
  maxCharacters?: number;
  variant?:
    | "input"
    | "select"
    | "color-picker"
    | "date"
    | "date-time"
    | "image"
    | "currency"
    | "phone-number"
    | "quantity-changer"
    | "time";
  dateType?: "SINGLE" | "RANGE";
  options?: CustomInputOptions[];
  labelClassName?: string;
  isLoading?: boolean;
  imageDomain?: string;
  searchValue?: string;
  onSearch?: (search: string) => void;
  details?: string[];
  minValue?: number;
  maxValue?: number;
  quantityChangerSize?: "small" | "default";
  minDate?: Date | null;
  maxDate?: Date | null;
  isMultipleSelection?: boolean;
  disabledInitOptionSelection?: boolean;
  prefix?: string;
  error?: boolean;
  createSelectionNewDataModalType?: CREATE_SELECTION_NEW_DATA_TYPE;
  disabledCreateSelectionNewData?: boolean;
  productCategoryId?: CreateSelectionNewDataModalProps["productCategoryId"];
  expenseAndIncomeType?: CreateSelectionNewDataModalProps["expenseAndIncomeType"];
  onValueChange?: (value: unknown) => void;
}

const colorTemplates = [
  "#A1C0AC",
  "#DFC7D7",
  "#F7E8F2",
  "#CAC9AF",
  "#F7F6DE",
  "#B67162",
  "#FFE7E2",
  "#FFA1A1",
];

const CustomInput: React.FC<CustomInputProps> = (props) => {
  const {
    form,
    name,
    labelText = "",
    placeholder,
    textArea,
    maxCharacters,
    type,
    variant,
    dateType = "SINGLE",
    options = [],
    labelClassName,
    isLoading,
    imageDomain = "",
    searchValue,
    onSearch,
    details = [],
    minValue = 0,
    maxValue,
    quantityChangerSize,
    isMultipleSelection,
    disabledInitOptionSelection,
    prefix,
    error,
    createSelectionNewDataModalType,
    disabledCreateSelectionNewData,
    productCategoryId,
    expenseAndIncomeType,
    onValueChange,
  } = props;

  const { showPassword, togglePasswordVisibility } = useCustomInputHandler();

  const {
    control,
    setValue,
    formState: { errors },
    watch,
  } = form;

  const values = watch();

  const [isOpen, setIsOpen] = React.useState(false);
  const [preview, setPreview] = React.useState<string | ArrayBuffer | null>(
    (typeof props?.defaultValue === "string" ? props.defaultValue : "") ?? ""
  );
  const [selectedTime, setSelectedTime] = React.useState({
    hour: "00",
    minute: "00",
  });
  const [
    isShowCreateSelectionNewDataModal,
    setIsShowCreateSelectionNewDataModal,
  ] = useState(false);

  const countryCodes = variant === "phone-number" ? getCountryCodes() : [];

  const hours = Array.from({ length: 24 }, (_, i) =>
    String(i).padStart(2, "0")
  );
  const minutes = Array.from({ length: 60 }, (_, i) =>
    String(i).padStart(2, "0")
  );

  const onDrop = React.useCallback(
    async (acceptedFiles: File[]) => {
      const reader = new FileReader();

      try {
        const data = await uploadFile({
          image: acceptedFiles[0],
          domain: imageDomain,
          type: "image",
          field: name,
        });

        reader.onload = () => setPreview(reader.result);
        reader.readAsDataURL(acceptedFiles[0]);
        form.setValue(name, data.key);
        form.clearErrors(name);
      } catch (error) {
        apiErrorHandler({ error, form });
        setPreview(null);
      }
    },
    [form, name, imageDomain]
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
  });

  const names = name?.split(".");

  let isError: any = errors;
  let value: any = values;

  if (names.length > 1) {
    for (const key of names) {
      if (isError && typeof isError === "object") {
        isError = isError[camelToSnake(key)];
      } else {
        isError = errors[camelToSnake(name)];
      }

      if (value && typeof value === "object" && key in value) {
        value = value[key];
      } else {
        value = value[key];
        break;
      }
    }
  } else {
    value = values[name];
    isError = errors[camelToSnake(name)];
  }

  useEffect(() => {
    if (name && values[`alreadyInit-${names.join("-")}`] === undefined) {
      setValue(`alreadyInit-${names.join("-")}`, true);
    }
  }, [name, names, values, setValue]);

  useEffect(() => {
    // Init Option Selection
    if (
      !disabledInitOptionSelection &&
      variant === "select" &&
      !isMultipleSelection &&
      options.length === 1 &&
      values[`alreadyInit-${names.join("-")}`]
    ) {
      const firstValue = options[0]?.value;

      setValue(name, firstValue, { shouldValidate: true });
      setValue(`alreadyInit-${names.join("-")}`, false);

      if (props.onChange) {
        props.onChange(firstValue as any);
      }
    }
  }, [
    disabledInitOptionSelection,
    variant,
    props,
    setValue,
    values,
    name,
    names,
    options,
  ]);

  useEffect(() => {
    // Init Preview
    if (variant === "image" && !preview) {
      setPreview(typeof value === "string" ? value : "");
    }
  }, [variant, value, preview]);

  useEffect(() => {
    // Init Time
    if (
      variant === "time" &&
      selectedTime.hour === "00" &&
      selectedTime.minute === "00"
    ) {
      if (typeof value === "string") {
        setSelectedTime({
          hour: value.split(":")[0],
          minute: value.split(":")[1],
        });
      }
    }
  }, [variant, selectedTime.hour, selectedTime.minute, value]);

  const formItemClass = `flex-col w-full ${!labelText ? "!space-y-0" : ""} ${
    props.disabled ? "cursor-not-allowed" : ""
  }`;

  const selectedSelectionOption = options?.find(
    (option) => `${option.value}` === `${value}`
  );

  const selectedOption = useDebounce({
    value: `${selectedSelectionOption?.label ?? placeholder}`,
    delay: 250,
  });

  if (variant === "select") {
    return (
      <FormField
        control={control}
        name={name}
        render={({ field }) => {
          const handleSelect = (value: string) => {
            if (options) {
              const selectionValue =
                typeof options[0]?.value === "number"
                  ? parseInt(value, 10)
                  : value;

              // Uncheck on click the selected row
              setValue(
                name,
                !!field.value &&
                  (typeof options[0]?.value === "number"
                    ? parseInt(field.value, 0)
                    : field.value) === selectionValue
                  ? null
                  : selectionValue,
                {
                  shouldValidate: true,
                }
              );
              setIsOpen(false);
            }
            if (props.onChange) {
              props.onChange(value as any);
            }
          };

          return (
            <>
              <FormItem className={formItemClass}>
                <FormLabel className={labelClassName}>{labelText}</FormLabel>

                <FormControl>
                  {isMultipleSelection ? (
                    <MultiSelect
                      defaultValue={field.value}
                      selectedOptions={options
                        .map((option) => ({
                          label: option.label,
                          placeholder: option.label,
                          value: `${option.value}`,
                        }))
                        .filter((option) =>
                          field.value.includes(`${option.value}`)
                        )}
                      onValueChange={(value) => {
                        form.setValue(
                          field.name,
                          Array.isArray(value) ? value : [value]
                        );

                        if (props.onChange) {
                          props.onChange(
                            Array.isArray(value) ? value : ([value] as any)
                          );
                        }
                      }}
                      options={options.map((option) => ({
                        ...option,
                        label: option.label,
                        placeholder: option.label,
                        value: `${option.value}`,
                      }))}
                      placeholder={`Select ${labelText}`}
                      normalHeight
                      searchValue={searchValue}
                      onSearch={onSearch}
                    />
                  ) : (
                    <Popover open={isOpen} onOpenChange={setIsOpen} modal>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant="link"
                            className={cn(
                              "w-full justify-between bg-white text-black pointer !no-underline border border-input rounded-md text-sm font-normal p-2.5 cursor-pointer overflow-hidden relative",
                              !value && "text-muted-foreground"
                            )}
                            disabled={props.disabled}
                            onClick={() =>
                              isLoading ? undefined : setIsOpen(!isOpen)
                            }
                          >
                            {selectedOption}

                            <div
                              className={cn(
                                "bg-white p-2.5 absolute right-0",
                                props.disabled &&
                                  "opacity-75 bg-darkIndicator-20"
                              )}
                            >
                              <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
                            </div>
                          </Button>
                        </FormControl>
                      </PopoverTrigger>

                      <PopoverContent className="w-[--radix-popover-trigger-width] max-h-[--radix-popover-content-available-height] p-0">
                        <Command className="w-full">
                          <CommandInput
                            placeholder={`Search ${labelText}`}
                            value={searchValue}
                            onValueChange={isLoading ? undefined : onSearch}
                          />

                          <CommandList>
                            {isLoading ? (
                              <CommandPrimitive.Loading>
                                <div className="flex items-center justify-center p-2">
                                  <Spinner />
                                </div>
                              </CommandPrimitive.Loading>
                            ) : null}

                            {options.length > 0 && !isLoading ? (
                              <CommandGroup
                                {...(props as any)}
                                value={
                                  field.value ? `${field.value}` : undefined
                                }
                                onValueChange={handleSelect}
                              >
                                {options.map((option) => {
                                  const userType = option?.userType;
                                  const customerType = option.customerType;
                                  const customerTypeHex = option?.typeHex;
                                  const activeContracts =
                                    option?.activeContracts;
                                  const isSalespersonAssigned =
                                    option.isSalespersonAssigned;

                                  return (
                                    <CommandItem
                                      value={`${option.label}`}
                                      key={option.value}
                                      onSelect={() => {
                                        handleSelect(`${option.value}`);
                                      }}
                                      className={cn(
                                        "w-full flex justify-between gap-4 py-3 mb-1"
                                      )}
                                    >
                                      <div className="flex flex-1 flex-row items-center">
                                        <Check
                                          className={cn(
                                            "mr-2 h-4 w-4",
                                            option.value === value
                                              ? "opacity-100"
                                              : "opacity-0"
                                          )}
                                        />
                                        <span className="font-normal text-sm">
                                          {option.label}
                                        </span>
                                      </div>

                                      <div className="flex flex-col items-end gap-2">
                                        {(userType ||
                                          isSalespersonAssigned) && (
                                          <div
                                            className="font-normal text-xs px-1 rounded text-right max-w-[200px]"
                                            style={{
                                              backgroundColor:
                                                isSalespersonAssigned
                                                  ? "#D4D4D4"
                                                  : userType === "customer"
                                                  ? customerTypeHex
                                                  : userType === "partner"
                                                  ? USER_TYPE_BACKGROUND.Partner
                                                  : USER_TYPE_BACKGROUND.Vendor,
                                            }}
                                          >
                                            {isSalespersonAssigned
                                              ? "Has an active salesperson"
                                              : userType === "customer"
                                              ? customerType
                                              : capitalize(userType)}
                                          </div>
                                        )}

                                        {isSalespersonAssigned && (
                                          <div className="max-w-56 text-xs text-muted-foreground text-right">
                                            {activeContracts?.map(
                                              (activeContract, index) => (
                                                <span key={index}>{`${
                                                  index !== 0 ? "and " : ""
                                                }From ${getSalespersonContractPeriod(
                                                  activeContract.contract_start_date,
                                                  activeContract.contract_end_date,
                                                  true
                                                )} `}</span>
                                              )
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    </CommandItem>
                                  );
                                })}
                              </CommandGroup>
                            ) : null}
                            {!isLoading ? (
                              <CommandPrimitive.Empty className="select-none rounded-sm px-2 py-3 text-center text-sm">
                                No Match Found
                              </CommandPrimitive.Empty>
                            ) : null}

                            {createSelectionNewDataModalType && (
                              <CommandPrimitive.List>
                                <div className="flex justify-end mt-1 border-t px-3">
                                  <Button
                                    disabled={disabledCreateSelectionNewData}
                                    variant="link"
                                    className="text-sm font-normal -mr-3.5"
                                    onClick={() =>
                                      setIsShowCreateSelectionNewDataModal(true)
                                    }
                                  >
                                    <AddCircle size={18} className="mr-1" />{" "}
                                    Create New Data
                                  </Button>
                                </div>
                              </CommandPrimitive.List>
                            )}
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  )}
                </FormControl>

                <FormMessage>{isError?.message}</FormMessage>
              </FormItem>

              {createSelectionNewDataModalType && (
                <CreateSelectionNewDataModal
                  isShow={isShowCreateSelectionNewDataModal}
                  onClose={() => setIsShowCreateSelectionNewDataModal(false)}
                  type={createSelectionNewDataModalType}
                  productCategoryId={productCategoryId}
                  expenseAndIncomeType={expenseAndIncomeType}
                />
              )}
            </>
          );
        }}
      />
    );
  } else if (variant === "color-picker") {
    return (
      <FormField
        control={control}
        name={name}
        render={({ field }) => {
          const handleSelectColorTemplate = (colorTemplate: string) => {
            setValue(name, colorTemplate, { shouldValidate: true });
          };

          const handleSelectColor = (value: string) => {
            setValue(name, value, { shouldValidate: true });
          };

          return (
            <FormItem className={formItemClass}>
              <FormLabel>{labelText}</FormLabel>

              <div className="flex">
                <div className="w-full grid grid-flow-col">
                  {colorTemplates.map((colorTemplate, index) => (
                    <div
                      className="w-10 h-10 rounded-full border cursor-pointer"
                      onClick={() => handleSelectColorTemplate(colorTemplate)}
                      style={{ backgroundColor: colorTemplate }}
                      key={index}
                    />
                  ))}
                </div>

                <FormControl>
                  <div className="flex items-center gap-2 border rounded-lg overflow-hidden pl-3 min-w-56">
                    <div className="flex flex-1 items-center gap-2">
                      <CgColorPicker size={24} />

                      <span className="">
                        {field.value ? field.value : "#FFFFFF"}
                      </span>
                    </div>

                    <ColorPicker
                      {...(props as any)}
                      value={field.value}
                      onChange={handleSelectColor}
                      className="w-24 rounded-l-none border-0"
                    />
                  </div>
                </FormControl>
              </div>

              <FormMessage>{form.formState.errors[name]?.message}</FormMessage>
            </FormItem>
          );
        }}
      />
    );
  } else if (variant === "date" || variant === "date-time") {
    return (
      <FormField
        control={control}
        name={name}
        render={({ field }) => {
          const handleSelectDate = (value: Date) => {
            setValue(name, value, { shouldValidate: true });
          };

          return (
            <FormItem className={formItemClass}>
              {labelText && (
                <FormLabel className={labelClassName}>{labelText}</FormLabel>
              )}

              <FormControl>
                <DateRangePicker
                  {...(props as any)}
                  date={value}
                  setDate={handleSelectDate}
                  placeholder={placeholder ?? "Select date"}
                  type={dateType}
                  className="flex h-10 w-full rounded-md border border-input bg-white px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-darkIndicator-20"
                  isIncludeTime={variant === "date-time"}
                  isShowNoEndDate
                />
              </FormControl>

              <FormMessage>{isError?.message}</FormMessage>
            </FormItem>
          );
        }}
      />
    );
  } else if (variant === "image") {
    return (
      <FormField
        control={form.control}
        name={name}
        render={() => (
          <FormItem className={formItemClass}>
            <FormLabel className={`${labelClassName}`}>{labelText}</FormLabel>

            <FormControl
              className="flex min-h-[80px] w-full rounded-md"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' stroke='%23e5e7eb' stroke-width='4' stroke-dasharray='6%2c 14' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e")`,
              }}
            >
              <div
                {...getRootProps()}
                className="flex cursor-pointer flex-col items-center justify-center gap-2"
              >
                {preview && (
                  <img
                    src={preview as string}
                    alt="preview"
                    className="max-h-[80px] rounded-lg"
                  />
                )}
                <GalleryEdit
                  size={24}
                  className={`${
                    preview ? "hidden" : "block"
                  } text-darkIndicator-40`}
                />
                <Input {...getInputProps()} type="file" />

                {!preview && (
                  <span className="text-sm font-normal text-darkIndicator-30 text-center">
                    {placeholder}
                  </span>
                )}
              </div>
            </FormControl>

            <FormMessage>{isError?.message}</FormMessage>
          </FormItem>
        )}
      />
    );
  } else if (variant === "phone-number") {
    return (
      <FormField
        control={control}
        name={name}
        render={({ field }) => {
          const phoneNumberValue = field.value;

          const selectedOption = field.value
            ? countryCodes.find((countryCode) =>
                phoneNumberValue.startsWith(countryCode.code)
              )
            : null;

          const phoneNumberInputValue = phoneNumberValue?.split(
            selectedOption?.code
          )[1];

          const handleSelectCountryCode = (countryCode: string) => {
            return setValue(name, `${countryCode}`, { shouldValidate: true });
          };

          const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            let newValue = e.target.value;

            return setValue(
              name,
              `${selectedOption?.code}${formatInputPhoneNumber(newValue)}`,
              {
                shouldValidate: true,
              }
            );
          };

          return (
            <FormItem className={formItemClass}>
              {labelText && (
                <FormLabel className={labelClassName}>{labelText}</FormLabel>
              )}

              <FormControl>
                <div className="grid grid-cols-[1fr_8fr] gap-4">
                  <Popover open={isOpen} onOpenChange={setIsOpen}>
                    <PopoverTrigger asChild>
                      <FormControl className="flex-1">
                        <span
                          className={cn(
                            "flex w-full justify-between bg-white border border-input rounded-md text-sm font-normal p-2.5 cursor-text",
                            !value && "text-muted-foreground"
                          )}
                          onClick={() =>
                            isLoading ? undefined : setIsOpen(!isOpen)
                          }
                        >
                          {value ? selectedOption?.code : "+62"}

                          <ChevronDown className="ml-1 h-4 w-4 shrink-0 opacity-50" />
                        </span>
                      </FormControl>
                    </PopoverTrigger>

                    <PopoverContent className=" max-h-[--radix-popover-content-available-height] p-0">
                      <Command className="w-full">
                        <CommandInput placeholder="Search" />

                        <CommandList>
                          {isLoading ? (
                            <CommandPrimitive.Loading>
                              <div className="flex items-center justify-center p-2">
                                <Spinner />
                              </div>
                            </CommandPrimitive.Loading>
                          ) : null}

                          <CommandGroup
                            {...(props as any)}
                            value={field.value ? `${field.value}` : undefined}
                          >
                            {countryCodes.map((countryCode) => (
                              <CommandItem
                                value={`(${countryCode.flag} ${countryCode.code}) ${countryCode.countryName}`}
                                key={countryCode.code}
                                onSelect={() => {
                                  handleSelectCountryCode(countryCode.code);
                                }}
                                className="pl-0"
                              >
                                <Check
                                  className={cn(
                                    "mr-2 h-4 w-4",
                                    countryCode.code === value
                                      ? "opacity-100"
                                      : "opacity-0"
                                  )}
                                />
                                {`(${countryCode.flag} ${countryCode.code}) ${countryCode.countryName}`}
                              </CommandItem>
                            ))}
                          </CommandGroup>

                          <CommandPrimitive.Empty className="select-none rounded-sm px-2 py-3 text-center text-sm">
                            No Match Found
                          </CommandPrimitive.Empty>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>

                  <Input
                    {...field}
                    placeholder={placeholder}
                    value={phoneNumberInputValue}
                    maxLength={maxCharacters}
                    onChange={onChange}
                    {...props}
                    className={(cn(props?.className || ""), "bg-wh")}
                  />
                </div>
              </FormControl>

              <FormMessage>{isError?.message}</FormMessage>
            </FormItem>
          );
        }}
      />
    );
  } else if (variant === "quantity-changer") {
    return (
      <FormField
        control={control}
        name={name}
        render={({ field }) => {
          const convertedValue =
            parseInt(value?.replace(/[^0-9]/g, ""), 10) || 0;

          const isAbleToDecrease = convertedValue > minValue;
          const isAbleToIncrease = convertedValue !== maxValue;

          const handleDecrease = () => {
            setValue(name, `${isAbleToDecrease ? convertedValue - 1 : 0}`, {
              shouldValidate: true,
            });
            onValueChange &&
              onValueChange(isAbleToDecrease ? convertedValue - 1 : 0);
          };

          const handleIncrease = () => {
            setValue(
              name,
              `${isAbleToIncrease ? convertedValue + 1 : maxValue}`,
              {
                shouldValidate: true,
              }
            );
            onValueChange &&
              onValueChange?.(isAbleToIncrease ? convertedValue + 1 : maxValue);
          };

          const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            let newValue = e.target.value;
            const value = e.target.value ?? "";
            newValue = value?.replace(/[^0-9.]/g, "");

            return setValue(name, newValue, { shouldValidate: true });
          };

          const iconSize = quantityChangerSize === "small" ? 20 : 28;
          const inputClassSize =
            quantityChangerSize === "small" ? "h-7 text-sm" : "text-lg";
          const inputSizeMoreThanTwo =
            quantityChangerSize === "small" ? 36 : 48;
          const inputSizeLessThanTwo =
            quantityChangerSize === "small" ? 28 : 40;
          const inputSizeMoreThanThree =
            quantityChangerSize === "small" ? 46 : 66;

          return (
            <FormItem className={formItemClass}>
              {labelText && (
                <FormLabel className={labelClassName}>{labelText}</FormLabel>
              )}

              <FormControl>
                <div className="flex items-center">
                  <Button
                    disabled={!isAbleToDecrease}
                    className={`px-2 outline-none ${
                      !isAbleToDecrease ? "text-darkIndicator-20" : ""
                    }`}
                    variant="link"
                    onClick={handleDecrease}
                  >
                    <MinusCirlce size={iconSize} />
                  </Button>

                  <Input
                    {...field}
                    value={numberWithCommas(field.value)}
                    maxLength={maxCharacters}
                    onChange={onChange}
                    min={0}
                    disabled={maxValue === 0}
                    {...props}
                    type="text"
                    className={`px-1 max-w-20 text-center ${inputClassSize}`}
                    style={{
                      width:
                        field?.value?.length <= 2
                          ? inputSizeLessThanTwo
                          : field?.value?.length > 3
                          ? inputSizeMoreThanThree
                          : inputSizeMoreThanTwo,
                    }}
                  />

                  <Button
                    disabled={!isAbleToIncrease}
                    className={`px-2 outline-none ${
                      !isAbleToIncrease ? "text-darkIndicator-20" : ""
                    }`}
                    variant="link"
                    onClick={handleIncrease}
                  >
                    <AddCircle size={iconSize} />
                  </Button>
                </div>
              </FormControl>

              <FormMessage>{isError?.message}</FormMessage>
            </FormItem>
          );
        }}
      />
    );
  } else if (variant === "time") {
    return (
      <FormField
        control={control}
        name={name}
        render={({ field }) => {
          const timeValue = `${selectedTime.hour}:${selectedTime.minute}`;

          const togglePicker = () => {
            if (isOpen) {
              setValue(name, timeValue, { shouldValidate: true });
            }
            setIsOpen(!isOpen);
          };
          const selectTime = (hour: any, minute: any) => {
            setSelectedTime({ hour, minute });
            setValue(name, `${hour}:${minute}`, { shouldValidate: true });
          };

          return (
            <FormItem
              className={`flex ${formItemClass} ${
                labelText ? "py-1.5 gap-1" : ""
              }`}
            >
              {labelText && (
                <FormLabel className={labelClassName}>{labelText}</FormLabel>
              )}

              <FormControl className="min-w-24">
                <div className="time-picker-container min-w-24">
                  <Input
                    {...(props as any)}
                    type="time"
                    value={field.value}
                    // onChange={handleSelectTime}
                    readOnly
                    // value={timeValue}
                    onClick={togglePicker}
                    placeholder={placeholder ?? "Select Time"}
                    className="!flex-none h-10 min-w-24 rounded-md border border-input bg-white px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 select:none"
                  />

                  <Clock className="absolute top-3 right-3" size={16} />

                  {isOpen && (
                    <div className="time-picker">
                      <div className="hour-picker">
                        {hours.map((hour) => (
                          <div
                            className="select:none"
                            key={hour}
                            onClick={() =>
                              selectTime(hour, selectedTime.minute)
                            }
                            style={{
                              backgroundColor:
                                hour === selectedTime.hour ? "#ffa1a1" : "",
                            }}
                          >
                            {hour}
                          </div>
                        ))}
                      </div>

                      <div className="minute-picker">
                        {minutes.map((minute) => (
                          <div
                            className="select:none"
                            key={minute}
                            onClick={() =>
                              selectTime(selectedTime.hour, minute)
                            }
                            style={{
                              backgroundColor:
                                minute === selectedTime.minute ? "#ffa1a1" : "",
                            }}
                          >
                            {minute}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </FormControl>

              <FormMessage className="text-wrap w-24">
                {isError?.message}
              </FormMessage>
            </FormItem>
          );
        }}
      />
    );
  }

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => {
        const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          let newValue = e.target.value;

          if (variant === "currency") {
            const value = convertCurrencyToString(e.target.value) ?? "";

            newValue = value?.replace(/[^0-9.]/g, "");

            if (newValue === "") newValue = "0";

            if (newValue && newValue.startsWith("0") && newValue.length > 1) {
              newValue = newValue.slice(1);
            }

            return setValue(name, newValue === "" ? "0" : newValue, {
              shouldValidate: true,
            });
          } else if (type === "number") {
            const value = e.target.value ?? "";

            newValue = value?.replace(/[^0-9.]/g, "");

            return setValue(name, newValue, { shouldValidate: true });
          }

          return setValue(name, newValue, { shouldValidate: true });
        };

        return (
          <FormItem className={formItemClass}>
            {labelText && (
              <FormLabel className={labelClassName}>{labelText}</FormLabel>
            )}

            <FormControl>
              <div className="relative">
                {!textArea ? (
                  <Input
                    prefix={prefix}
                    {...field}
                    placeholder={placeholder}
                    value={
                      variant === "currency"
                        ? convertToCurrency(field.value)
                        : type === "number"
                        ? numberWithCommas(field.value)
                        : field.value
                    }
                    maxLength={maxCharacters}
                    error={error}
                    onChange={onChange}
                    {...(props?.type === "number" && {
                      min: 0,
                    })}
                    {...props}
                    type={
                      type === "password"
                        ? showPassword
                          ? "text"
                          : "password"
                        : type === "number"
                        ? "text"
                        : type
                    }
                    className={(cn(props?.className || ""), "bg-wh")}
                  />
                ) : (
                  <Textarea
                    {...field}
                    placeholder={placeholder}
                    value={field.value}
                    maxLength={maxCharacters}
                    {...(props as any)}
                    className={(cn(props?.className || ""), "bg-wh")}
                  />
                )}

                {type === "password" && (
                  <Button
                    type="button"
                    onClick={togglePasswordVisibility}
                    className="absolute top-0 right-0 bg-transparent hover:bg-transparent"
                  >
                    {showPassword ? <Eye size={20} /> : <EyeSlash size={20} />}
                  </Button>
                )}
              </div>
            </FormControl>

            {maxCharacters && (
              <span className="text-sm font-normal text-darkIndicator-40">
                <>{`${field?.value?.length ?? 0}/${numberWithCommas(
                  maxCharacters
                )} Characters`}</>
              </span>
            )}

            {details.length > 0 && (
              <div>
                {details.map((detail, index) => (
                  <div className="!-mt-1" key={index}>
                    <span className="text-sm font-normal text-darkIndicator-40">
                      {detail}
                    </span>
                  </div>
                ))}
              </div>
            )}

            <FormMessage>{isError?.message}</FormMessage>
          </FormItem>
        );
      }}
    />
  );
};

export default CustomInput;
